/**
 * Post-Ingestion QC Review — frontend API client.
 *
 * Mirrors the 7 endpoints in `backend/crates/evidenx-api/src/handlers/qc.rs`.
 * Uses the project's typed-generics pattern on `apiClient` (anchor:
 * `frontend/src/services/ingestion.ts:20-21`). No zod.
 *
 * The `exportQcRun` function bypasses `apiClient` because the response is a
 * binary download (CSV / JSON file body with `Content-Disposition: attachment`).
 * It uses a direct `fetch` with the same bearer-token auth header pattern.
 */

import { apiClient, ApiError } from './api'
import type {
  AcknowledgeRequest,
  ExportFormat,
  ForceRerunRequest,
  ListQcRunsResponse,
  QcCheckResult,
  QcDashboardAlertsResponse,
  QcRunWithResults,
} from '../types/qc'

import { API_BASE_URL } from './api-base-url'
const ACCESS_TOKEN_KEY = 'evidenx_access_token'

// ---------------------------------------------------------------------------
// Case-scoped endpoints
// ---------------------------------------------------------------------------

/** `POST /api/cases/:caseId/ingestion-runs/:runId/qc-runs` */
export async function runQcChecks(
  caseId: string,
  runId: string
): Promise<QcRunWithResults> {
  return apiClient.post<QcRunWithResults>(
    `/cases/${caseId}/ingestion-runs/${runId}/qc-runs`
  )
}

/** `GET /api/cases/:caseId/ingestion-runs/:runId/qc-runs/latest` (returns `null` on 404). */
export async function getLatestQcRun(
  caseId: string,
  runId: string
): Promise<QcRunWithResults | null> {
  try {
    return await apiClient.get<QcRunWithResults>(
      `/cases/${caseId}/ingestion-runs/${runId}/qc-runs/latest`
    )
  } catch (err) {
    if (err instanceof ApiError && err.isNotFound) return null
    throw err
  }
}

/** `GET /api/cases/:caseId/ingestion-runs/:runId/qc-runs` */
export async function listQcRuns(
  caseId: string,
  runId: string
): Promise<ListQcRunsResponse> {
  return apiClient.get<ListQcRunsResponse>(
    `/cases/${caseId}/ingestion-runs/${runId}/qc-runs`
  )
}

/** `POST .../qc-runs/:qcRunId/results/:resultId/acknowledge` */
export interface AcknowledgeQcResultParams {
  caseId: string
  runId: string
  qcRunId: string
  resultId: string
  reason: string
}

export async function acknowledgeQcResult(
  params: AcknowledgeQcResultParams
): Promise<QcCheckResult> {
  const body: AcknowledgeRequest = { reason: params.reason }
  return apiClient.post<QcCheckResult>(
    `/cases/${params.caseId}/ingestion-runs/${params.runId}/qc-runs/${params.qcRunId}/results/${params.resultId}/acknowledge`,
    body
  )
}

/** `POST /api/cases/:caseId/ingestion-runs/:runId/qc-rerun` */
export interface ForceQcRerunParams {
  caseId: string
  runId: string
  reason: string
}

export async function forceQcRerun(params: ForceQcRerunParams): Promise<void> {
  const body: ForceRerunRequest = { reason: params.reason }
  // Handler returns 204 No Content; apiClient resolves to `undefined`, which
  // we discard. Typed as `unknown` because `void` is not a valid generic arg.
  await apiClient.post<unknown>(
    `/cases/${params.caseId}/ingestion-runs/${params.runId}/qc-rerun`,
    body
  )
}

// ---------------------------------------------------------------------------
// Export — binary download
// ---------------------------------------------------------------------------

/** Both the blob body and the server-provided filename — caller saves the file. */
export interface QcExportDownload {
  blob: Blob
  filename: string
}

export interface ExportQcRunParams {
  caseId: string
  runId: string
  qcRunId: string
  format: ExportFormat
}

/**
 * `GET .../qc-runs/:qcRunId/export?format=csv|json`
 *
 * Returns the raw response body as a `Blob` plus the filename extracted from
 * the `Content-Disposition` header so a caller can trigger a download via
 * `URL.createObjectURL`.
 *
 * Uses a direct `fetch` rather than `apiClient` because the response is
 * binary, not JSON. Auth header and `API_BASE_URL` are re-used from the
 * canonical client to keep behaviour aligned with token refresh elsewhere.
 */
export async function exportQcRun(
  params: ExportQcRunParams
): Promise<QcExportDownload> {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY)
  const url = `${API_BASE_URL}/cases/${params.caseId}/ingestion-runs/${params.runId}/qc-runs/${params.qcRunId}/export?format=${params.format}`

  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  if (!response.ok) {
    let body: { error: string; message: string; messageKey?: string } | null =
      null
    try {
      body = (await response.json()) as typeof body
    } catch {
      // Body was not JSON (e.g. truncated response) — leave as null.
    }
    throw new ApiError(response.status, response.statusText, body)
  }

  const blob = await response.blob()
  const filename = extractFilename(response.headers.get('Content-Disposition')) ??
    `qc-report-${params.runId}-${params.qcRunId}.${params.format}`

  return { blob, filename }
}

/**
 * Pull `filename` out of a `Content-Disposition: attachment; filename="..."`
 * header. Tolerates the unquoted form and the RFC 5987 `filename*=` extension.
 */
function extractFilename(header: string | null): string | null {
  if (!header) return null
  // filename*=UTF-8''... (RFC 5987) — prefer this if present.
  const ext = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (ext?.[1]) return decodeURIComponent(ext[1].trim())
  // filename="..." (quoted) and filename=... (unquoted).
  const quoted = /filename="([^"]+)"/i.exec(header)
  if (quoted?.[1]) return quoted[1]
  const bare = /filename=([^;]+)/i.exec(header)
  if (bare?.[1]) return bare[1].trim()
  return null
}

// ---------------------------------------------------------------------------
// Dashboard endpoint
// ---------------------------------------------------------------------------

/** `GET /api/dashboard/qc-alerts` */
export async function listQcDashboardAlerts(): Promise<QcDashboardAlertsResponse> {
  return apiClient.get<QcDashboardAlertsResponse>(`/dashboard/qc-alerts`)
}
